import React, { useEffect, useRef } from 'react';
import AnalyticsTracker, { AnalyticsEvent, AnalyticsUser, AnalyticsEvents } from '../utils/analytics';

// Global analytics instance
let analyticsInstance: AnalyticsTracker | null = null;

// Initialize analytics instance
function getAnalyticsInstance(): AnalyticsTracker {
  if (!analyticsInstance) {
    analyticsInstance = new AnalyticsTracker();
  }
  return analyticsInstance;
}

// React hook for analytics
export function useAnalytics() {
  const analyticsRef = useRef<AnalyticsTracker | null>(null);

  useEffect(() => {
    analyticsRef.current = getAnalyticsInstance();
  }, []);

  const track = (event: AnalyticsEvent) => {
    if (analyticsRef.current) {
      analyticsRef.current.track(event);
    }
  };

  const identify = (user: AnalyticsUser) => {
    if (analyticsRef.current) {
      analyticsRef.current.identify(user);
    }
  };

  const setUserProperties = (properties: Record<string, any>) => {
    if (analyticsRef.current) {
      analyticsRef.current.setUserProperties(properties);
    }
  };

  const reset = () => {
    if (analyticsRef.current) {
      analyticsRef.current.reset();
    }
  };

  return {
    track,
    identify,
    setUserProperties,
    reset,
    isReady: () => analyticsRef.current?.isReady() || false,
    getStatus: () => analyticsRef.current?.getStatus() || {},
    AnalyticsEvents,
  };
}

// Convenience functions for common tracking patterns
export const trackPageView = (pageName: string, properties?: Record<string, any>) => {
  const analytics = getAnalyticsInstance();
  analytics.track({
    name: AnalyticsEvents.PAGE_VIEW,
    properties: {
      page_name: pageName,
      page_url: window.location.href,
      ...properties,
    },
  });
};

export const trackButtonClick = (buttonName: string, properties?: Record<string, any>) => {
  const analytics = getAnalyticsInstance();
  analytics.track({
    name: AnalyticsEvents.BUTTON_CLICK,
    properties: {
      button_name: buttonName,
      ...properties,
    },
  });
};

export const trackFormSubmit = (formName: string, properties?: Record<string, any>) => {
  const analytics = getAnalyticsInstance();
  analytics.track({
    name: AnalyticsEvents.FORM_SUBMIT,
    properties: {
      form_name: formName,
      ...properties,
    },
  });
};

export const trackError = (error: Error, context?: string) => {
  const analytics = getAnalyticsInstance();
  analytics.track({
    name: AnalyticsEvents.ERROR_OCCURRED,
    properties: {
      error_message: error.message,
      error_stack: error.stack,
      context,
    },
  });
};

export const trackFeatureUsage = (featureName: string, properties?: Record<string, any>) => {
  const analytics = getAnalyticsInstance();
  analytics.track({
    name: AnalyticsEvents.FEATURE_USED,
    properties: {
      feature_name: featureName,
      ...properties,
    },
  });
};

// HOC for page tracking
export function withPageTracking<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  pageName: string
) {
  return function WithPageTrackingComponent(props: P) {
    useEffect(() => {
      trackPageView(pageName);
    }, [pageName]);

    return React.createElement(WrappedComponent, props);
  };
}

// Hook for page tracking
export function usePageTracking(pageName: string) {
  useEffect(() => {
    trackPageView(pageName);
  }, [pageName]);
} 